const isActive = (row) =>
  row && (row.is_active === undefined || Number(row.is_active) === 1);

const allowedPurpose = (row, purposes) =>
  !purposes || purposes.includes(row.purpose);

const conversionGraph = (rows, purposes = null) => {
  const graph = new Map();

  (rows || []).forEach((row) => {
    if (!isActive(row) || !allowedPurpose(row, purposes)) return;

    const fromUnitId = Number(row.from_unit_id);
    const toUnitId = Number(row.to_unit_id);
    const fromQuantity = Number(row.from_quantity);
    const toQuantity = Number(row.to_quantity);

    if (!fromUnitId || !toUnitId || !(fromQuantity > 0) || !(toQuantity > 0)) {
      return;
    }

    if (!graph.has(fromUnitId)) graph.set(fromUnitId, []);
    if (!graph.has(toUnitId)) graph.set(toUnitId, []);

    graph.get(fromUnitId).push({
      unitId: toUnitId,
      factor: toQuantity / fromQuantity,
    });
    graph.get(toUnitId).push({
      unitId: fromUnitId,
      factor: fromQuantity / toQuantity,
    });
  });

  return graph;
};

export const conversionFactor = (
  rows,
  fromUnitId,
  toUnitId,
  purposes = null
) => {
  const fromId = Number(fromUnitId);
  const toId = Number(toUnitId);
  if (!fromId || !toId) return null;
  if (fromId === toId) return 1;

  const graph = conversionGraph(rows, purposes);
  const queue = [{ unitId: fromId, factor: 1 }];
  const visited = new Set([fromId]);

  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    for (const edge of graph.get(current.unitId) || []) {
      if (visited.has(edge.unitId)) continue;

      const factor = current.factor * edge.factor;
      if (edge.unitId === toId) return factor;

      visited.add(edge.unitId);
      queue.push({ unitId: edge.unitId, factor });
    }
  }

  return null;
};

// Cost of one recipe (BOM) line.
//
// A line's `quantity` is in the usage unit the recipe was written in, while the
// ingredient's `unit_cost` is per base (stock) unit — multiplying them directly
// overcharges every line whose usage unit differs from the base unit. The API
// ships a ready `line_cost`; the local conversion is the fallback for lines the
// user just added in the editor, which have not been through the API yet.
export const recipeLineCost = (line, ingredients) => {
  if (!line) return 0;
  if (line.line_cost !== undefined && line.line_cost !== null) {
    return Number(line.line_cost) || 0;
  }

  const quantity = Number(line.quantity) || 0;
  const ingredient = (ingredients || []).find(
    (candidate) => Number(candidate.id) === Number(line.ingredient_id)
  );
  if (!ingredient) return 0;

  const unitCost = Number(ingredient.unit_cost) || 0;
  const factor = conversionFactor(
    ingredient.unit_conversions || [],
    line.unit_id,
    ingredient.unit_id,
    ["usage"]
  );

  return factor === null ? 0 : quantity * factor * unitCost;
};

// "10 Pack / 1 gram" — a base-unit amount read in the largest unit available,
// with the leftover in the base unit. `units` is a list of { label, factor }
// where factor is how many base units one of them holds. Falls back to the
// plain base amount when there is no larger unit, or when the amount does not
// reach one whole unit.
export const stockUnitText = (
  baseQuantity,
  units,
  baseLabel,
  formatNumber = (value) => String(value)
) => {
  const quantity = Number(baseQuantity || 0);
  const baseText = `${formatNumber(quantity)} ${baseLabel}`;
  if (!(quantity > 0)) return baseText;

  const largest = (units || []).reduce((best, unit) => {
    if (!(unit.factor > 1)) return best;
    return !best || unit.factor > best.factor ? unit : best;
  }, null);
  if (!largest) return baseText;

  const whole = Math.floor(quantity / largest.factor);
  if (whole < 1) return baseText;

  // Guard against float drift, e.g. 0.30000000000000004 leftovers.
  const remainder = Math.round((quantity - whole * largest.factor) * 1e6) / 1e6;
  const wholeText = `${formatNumber(whole)} ${largest.label}`;
  if (remainder <= 0) return wholeText;

  return `${wholeText} / ${formatNumber(remainder)} ${baseLabel}`;
};

export const connectedUnitIds = (rows, startUnitId, purposes = null) => {
  const startId = Number(startUnitId);
  if (!startId) return [];

  const graph = conversionGraph(rows, purposes);
  const queue = [startId];
  const visited = new Set([startId]);

  for (let index = 0; index < queue.length; index += 1) {
    for (const edge of graph.get(queue[index]) || []) {
      if (visited.has(edge.unitId)) continue;
      visited.add(edge.unitId);
      queue.push(edge.unitId);
    }
  }

  return Array.from(visited);
};

export const hasConversionCycle = (rows) => {
  const parent = new Map();

  const root = (unitId) => {
    let current = unitId;
    while (parent.get(current) !== current) {
      parent.set(current, parent.get(parent.get(current)));
      current = parent.get(current);
    }
    return current;
  };

  for (const row of rows || []) {
    if (!isActive(row)) continue;
    const fromUnitId = Number(row.from_unit_id);
    const toUnitId = Number(row.to_unit_id);
    if (!fromUnitId || !toUnitId) continue;

    if (!parent.has(fromUnitId)) parent.set(fromUnitId, fromUnitId);
    if (!parent.has(toUnitId)) parent.set(toUnitId, toUnitId);

    const fromRoot = root(fromUnitId);
    const toRoot = root(toUnitId);
    if (fromRoot === toRoot) return true;
    parent.set(fromRoot, toRoot);
  }

  return false;
};
